import { InputField } from '@next-components/input-field';
import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { CustomerType, IBosCustomer, validate, VALIDATION_TYPE } from 'next-common';
import {
  fieldKeys,
  getFieldValidationType,
  getInputType,
  getUniqueList,
  ICustomerForm,
  isAllFieldsValid,
} from './CustomerForm-utils';
import { CustomerFormStyled } from '../CustomerPage.styled';

const DEBOUNCE_DELAY = 500;
const formFields = new Map();

export const CustomerForm = (props: ICustomerForm) => {
  const [validFields, setValidFields] = useState<string[]>([]);
  const [modifiedFields, setModifiedFields] = useState<string[]>([]);
  const [initialState, setInitialState] = useState(false);
  const isBusinessCustomer = props.customerType === CustomerType.BUSINESS
  const phoneRegex = /^(|((00|\+)?47)?\d{8})$/
  const emailRegex = /^(|(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))$/;


  const setEmptyCustomerFields = () => {
    fieldKeys(props.customerType)
      .filter((key) => !props.customer[key])
      .forEach((key) => setValidFields(validFields.filter((fieldKey) => fieldKey !== key)));
  };

  const setValidCustomerFields = () => {
    if (!props.customer) {
      return;
    }
    const fields = [];
    fieldKeys(props.customerType)
      .filter((key) => !!props.customer[key])
      .forEach((key) => {
        const fieldValidationType = getFieldValidationType(props.customerType, key);
        if (validate({ value: props.customer[key], type: fieldValidationType })) {
          fields.push(key);
        }
      });
    if (isBusinessCustomer) {
      fields.push('phoneMobile');
      fields.push('email');
    }
    setValidFields(Array.from(new Set(fields)));
  };

  useEffect(() => {
    if (props.customerType === CustomerType.PRIVATE) {
      formFields.delete('organizationName');
      formFields.delete('organizationNo');
    }
    if (props.customerType === CustomerType.BUSINESS) {
      formFields.delete('firstName');
      formFields.delete('lastName');
    }
  }, [props.customerType, props.customer]);

  useEffect(() => {
    if (props.customer) {
      setEmptyCustomerFields();
      setValidCustomerFields();
    }
    props.updateValidation(isAllFieldsValid(props.customerType, validFields));
  }, []);


  useEffect(() => {
    if (isBusinessCustomer) {
      formFields.set('phoneMobile', props?.customer?.phoneMobile);
      formFields.set('email', props?.customer?.email);
    }
  }, []);

  useEffect(() => {
    if (!props.customer) {
      return;
    }
    if (isAllFieldsValid(props.customerType, validFields)) {
      props.updatedFormData(getModifiedFields());
    }
    props.updateValidation(isAllFieldsValid(props.customerType, validFields));
  }, [validFields, modifiedFields]);

  useEffect(() => {
    setInitialState(true);
    setValidCustomerFields();
    if (!props.customer) setModifiedFields([]);
    setTimeout(() => {
      setInitialState(false);
      if (props?.customer?.type) setModifiedFields([]);
    }, DEBOUNCE_DELAY + 10);
  }, [props.customer]);

  const getModifiedFields = (): Partial<IBosCustomer> => {
    const modified: string[] = fieldKeys(props.customerType).filter(
      (key) => validFields.includes(key) && modifiedFields.includes(key),
    );
    return modified.reduce((changes, key) => {
      return {
        ...changes,
        [key]: formFields.get(key),
      };
    }, {});
  };

  const isValidated = (key: string): boolean => {
    return validFields.includes(key) && modifiedFields.includes(key);
  };

  const isInputError = (key: string): string => {
    return validFields.includes(key) ? null : 'Ugyldig verdi';
  };


  const checkValidation = (type, value) => {
    if (type === VALIDATION_TYPE.PHONENUMBER) {
      return phoneRegex.test(value);
    } else if (type === VALIDATION_TYPE.EMAIL) {
      return emailRegex.test(value);
    }
  };

  const onChange = (value, type, key) => {
    formFields.set(key, value);
    if (!initialState) {
      setModifiedFields(getUniqueList(modifiedFields, key));
    }
    const validChange = validate({ value, type });
    const fieldIndex = validFields.indexOf(key);
    // Always remove the field from the validFields array
    if (fieldIndex > -1) {
      const newValidFields = [...validFields];
      newValidFields.splice(fieldIndex, 1);
      setValidFields(newValidFields);
    }
    // Add the field to the validFields array if the change is valid
    if (validChange || (isBusinessCustomer && (key === "phoneMobile" ? checkValidation(type, value) : (key === "email" && checkValidation(type, value))))) {
      setValidFields(getUniqueList(validFields, key));
    }
    props.updateValidation(isAllFieldsValid(props.customerType, validFields));
  };

  const getField = (
    key: string,
    label: string,
    isDisabled: boolean = false,
    validationType: VALIDATION_TYPE = VALIDATION_TYPE.STRING,
    placeholder: string = null,
    initialValue = null,
  ) => {
    if (!initialValue) {
      initialValue = !!props.customer && props.customer?.hasOwnProperty(key) ? props.customer[key] : null;
    }
    return (
      <InputField
        onChange={debounce((e) => onChange(e, validationType, key), DEBOUNCE_DELAY)}
        placeholder={placeholder || label}
        label={label}
        withErrorMessage
        type={getInputType(validationType)}
        defaultValue={initialValue}
        isValid={isValidated(key)}
        errorMessage={isInputError(key)}
        startAdornment={validationType === VALIDATION_TYPE.PHONENUMBER ? '+47' : null}
        disabled={isDisabled}
        className="borderLight"
        hideValidationIcon={isBusinessCustomer && (key === 'phoneMobile' || key === 'email') && !formFields.get(key)}
      />
    );
  };

  return (
    <CustomerFormStyled>
      <div className="inputGroup">
        {props.customerType === CustomerType.BUSINESS
          ? getField('organizationName', 'Firmanavn', true)
          : getField('firstName', 'Fornavn')}
        {props.customerType === CustomerType.BUSINESS
          ? getField('organizationNo', 'Org.nr', true, VALIDATION_TYPE.ORG_NR, null, props.customer?.organizationNo)
          : getField('lastName', 'Etternavn')}
      </div>

      <div className="inputGroup">
        {getField('phoneMobile', 'Telefonnummer', false, VALIDATION_TYPE.PHONENUMBER, props.customer?.phoneMobile, null)}
        {getField('email', 'E-post', false, VALIDATION_TYPE.EMAIL, null, null )}
      </div>

      <div className="inputGroup threeGrid">
        {getField('zipCode', 'Postnr.', false, VALIDATION_TYPE.ZIPCODE, '0123')}
        {getField('city', 'Poststed')}
        {getField('countryCode', 'Land')}
      </div>
    </CustomerFormStyled>
  );
};
